import { ControlRecord, IOwnerProfileSession, IPictureItem } from '../ts/Definitions';
import { ProfileRole } from '../ts/enums';
import Database from '../utilities/database';
import { getProfileImage } from '../utilities/imageUtils';

class BaseSettings {
    protected database: Database;
    constructor(database: Database) {
        this.database = database;
    }

    public getControl = () => ({} as unknown as ControlRecord);


    public async getDisplayImage(item: IPictureItem, defaultImage: string): Promise<string> {
        if (item.img) return item.img;
        if (!item.imgId) return defaultImage;
        item.img = await this.lookupImage(item.imgId);
        if (item.img && item.img.includes('?') && !item.img.includes('gstatic')) {
            const parts = item.img.split('?');
            if (parts.length === 2) {
                item.img = parts[0]; // drop the params
            }
        }
        return item.img ?? defaultImage;
    }

    public lookupImage(id: string): Promise<string> {
        return this.database.getImage(id);
    }

    public getAllImageDbEntries = async (): Promise<Record<string, string>> => {
        const result = await this.database.getTableSnap<Record<string, string> | null>("imageDb");
        return result ?? {};
    };

    public importImageDb = async (
        data: Record<string, string>,
        replaceOnMatch: boolean,
        onProgress?: (done: number, total: number) => void,
    ): Promise<{ inserted: number; replaced: number; skipped: number }> => {
        const existing = await this.database.getTableSnap<Record<string, string> | null>("imageDb");
        const existingMap: Record<string, string> = existing ?? {};

        const toUpdate: { imgId: string; img: string }[] = [];
        let inserted = 0;
        let replaced = 0;
        let skipped = 0;

        for (const [uuid, url] of Object.entries(data)) {
            if (!uuid || !url) continue;
            if (uuid in existingMap) {
                if (replaceOnMatch) {
                    toUpdate.push({ imgId: uuid, img: url });
                    replaced++;
                } else {
                    skipped++;
                }
            } else {
                toUpdate.push({ imgId: uuid, img: url });
                inserted++;
            }
        }

        // Firebase rejects writes that are too large, so send in chunks of 200 entries
        const CHUNK_SIZE = 200;
        for (let i = 0; i < toUpdate.length; i += CHUNK_SIZE) {
            await this.database.updateBatchImageDb(toUpdate.slice(i, i + CHUNK_SIZE));
            onProgress?.(Math.min(i + CHUNK_SIZE, toUpdate.length), toUpdate.length);
        }

        return { inserted, replaced, skipped };
    };


    public convertOwnerProfileToCommunityProfile = (user: ControlRecord,
        ownerProfile?: IOwnerProfileSession): ControlRecord => {
        if (!ownerProfile) return user;

        return new ControlRecord({
            ...user,
            username: ownerProfile.name,
            summary: ownerProfile.description,
            profileImage: ownerProfile.ownerImg ?? getProfileImage(user),
            categories: ownerProfile.categories,
            missingCategories: ownerProfile.missingCategories,
            role: ProfileRole.Owner,
        });
    }

    protected convertSwitchProfileToCommunityProfile = (user: ControlRecord, ownerProfile?: IOwnerProfileSession): ControlRecord => {
        if (!ownerProfile) return user;

        return new ControlRecord({
            ...user,
            profileImage: user.profileImage || ownerProfile.ownerImg || getProfileImage(user),
            role: ProfileRole.Switch,
        });
    }

    public getMemberCommunityProfile(user: ControlRecord): ControlRecord {
        if (!user) return user;
        const role = this.getCommunityRole(user, []);
        return new ControlRecord({
            ...user,
            role: role,
        });
    }
    public getAllControlRecords = (): ControlRecord[] => {
        return [];
    }

    public getCommunityRole = (user: ControlRecord, profiles: IOwnerProfileSession[]): ProfileRole => {
        if (!user || !user.userid) return ProfileRole.None;
        const ownerProfile = profiles.find(p => p && p.userid && p.userid.includes(user.userid));
        const allMembers = this.getAllControlRecords();
        const ownedProfiles = allMembers.filter(p => p && p.userid && p.ownerid === user.userid && p.ownerid !== p.userid);
        const hasOwnership = !!(ownerProfile || ownedProfiles.length > 0);
        const role = user.role;
        // Only recalculate when: no role yet, Owner lost all indicators, or Member gained indicators
        const needsRecalculation =
            !role ||
            (!hasOwnership && role === ProfileRole.Owner) ||
            (hasOwnership && role === ProfileRole.Member);
        if (!needsRecalculation) return role;
        if (hasOwnership) {
            // Admin with no prior role and an owner profile gets Owner; otherwise Switch
            return (!role && user.isAdmin) ? ProfileRole.Owner : ProfileRole.Switch;
        }
        return ProfileRole.Member;
    }

    public isSystemLoggedOn = () => false;

    public getCommunityProfiles = (): ControlRecord[] => {
        const users = this.getAllControlRecords();
        const returnProfiles: ControlRecord[] = [];
        users.forEach((user) => {
            if ((!this.isSystemLoggedOn() && user.userid === this.database.getSystemUser()?.userid) || !user.username) return;
            const profile = this.getMemberCommunityProfile(user);
            if (!profile.username) return;
            returnProfiles.push(profile);
        });
        return returnProfiles.sort((x, y) => (x.username ?? '').localeCompare(y.username ?? ''));
    }


}

export default BaseSettings;